import React, { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const Navbar = ({ t, language, setLanguage }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const languages = [
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'zh', name: '中文', flag: '🇨🇳' }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsOpen(false);
    }
  };

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-black/95 backdrop-blur-md shadow-lg border-b border-gold/20' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_miguel-properties/artifacts/0ireamta_foto.png"
              alt="Logo"
              className="h-8 w-8 object-contain"
            />
            <h1 className={`text-2xl font-bold transition-all ${
              scrolled ? 'text-gold' : 'text-white'
            } heading-luxury`}>
              Miguel Ângelo
            </h1>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('home')} 
              className={`transition-colors font-medium text-sm tracking-wide ${
                scrolled ? 'text-gray-300 hover:text-gold' : 'text-white hover:text-gold'
              }`}
              data-testid="nav-home-btn"
            >
              {t.nav.home}
            </button>
            <button 
              onClick={() => scrollToSection('team')} 
              className={`transition-colors font-medium text-sm tracking-wide ${
                scrolled ? 'text-gray-300 hover:text-gold' : 'text-white hover:text-gold'
              }`}
              data-testid="nav-team-btn"
            >
              {t.nav.team}
            </button>
            <button 
              onClick={() => scrollToSection('services')} 
              className={`transition-colors font-medium text-sm tracking-wide ${
                scrolled ? 'text-gray-300 hover:text-gold' : 'text-white hover:text-gold'
              }`}
              data-testid="nav-services-btn"
            >
              {t.nav.services}
            </button>
            <a 
              href="https://predimed.pt"
              target="_blank"
              rel="noopener noreferrer"
              className={`transition-colors font-medium text-sm tracking-wide ${
                scrolled ? 'text-gray-300 hover:text-gold' : 'text-white hover:text-gold'
              }`}
              data-testid="nav-predimed-link"
            >
              {t.nav.predimed}
            </a>
            <a 
              href="/simulador" 
              className={`transition-colors font-medium text-sm tracking-wide ${
                scrolled ? 'text-gray-300 hover:text-gold' : 'text-white hover:text-gold'
              }`}
              data-testid="nav-simulator-link"
            >
              Simulador
            </a>
            <a 
              href="/primes" 
              className={`transition-colors font-medium text-sm tracking-wide ${
                scrolled ? 'text-gray-300 hover:text-gold' : 'text-white hover:text-gold'
              }`}
              data-testid="nav-primes-link"
            >
              Primes
            </a>
            <a 
              href="/projectos" 
              className={`transition-colors font-medium text-sm tracking-wide ${
                scrolled ? 'text-gray-300 hover:text-gold' : 'text-white hover:text-gold'
              }`}
              data-testid="nav-projectos-link"
            >
              Projectos
            </a>
            <a 
              href="/business"
              className={`transition-colors font-medium text-sm tracking-wide ${
                scrolled ? 'text-gray-300 hover:text-gold' : 'text-white hover:text-gold'
              }`}
              data-testid="nav-business-link"
            >
              Área de Negócio
            </a>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="gap-2 bg-gold/10 border-gold/30 text-gold hover:bg-gold hover:text-black"
                >
                  <Globe className="h-4 w-4" />
                  {languages.find(l => l.code === language)?.flag}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-black/95 border-gold/30">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`text-gray-300 hover:text-gold hover:bg-gold/10 ${
                      language === lang.code ? 'bg-gold/20 text-gold' : ''
                    }`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="bg-gold/10 border-gold/30">
                  <Globe className="h-4 w-4 text-gold" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-black/95 border-gold/30">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`text-gray-300 hover:text-gold hover:bg-gold/10 ${
                      language === lang.code ? 'bg-gold/20 text-gold' : ''
                    }`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <button onClick={() => setIsOpen(!isOpen)} className="text-gold">
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden pb-4 space-y-2 bg-black/95 rounded-b-lg border-b border-gold/20">
            <button 
              onClick={() => scrollToSection('home')} 
              className="block w-full text-left px-4 py-3 text-gray-300 hover:bg-gold/10 hover:text-gold rounded-md transition-colors"
              data-testid="mobile-nav-home"
            >
              {t.nav.home}
            </button>
            <button 
              onClick={() => scrollToSection('team')} 
              className="block w-full text-left px-4 py-3 text-gray-300 hover:bg-gold/10 hover:text-gold rounded-md transition-colors"
              data-testid="mobile-nav-team"
            >
              {t.nav.team}
            </button>
            <button 
              onClick={() => scrollToSection('services')} 
              className="block w-full text-left px-4 py-3 text-gray-300 hover:bg-gold/10 hover:text-gold rounded-md transition-colors"
              data-testid="mobile-nav-services"
            >
              {t.nav.services}
            </button>
            <a 
              href="https://predimed.pt"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-left px-4 py-3 text-gray-300 hover:bg-gold/10 hover:text-gold rounded-md transition-colors"
              data-testid="mobile-nav-predimed"
            >
              {t.nav.predimed}
            </a>
            <a 
              href="/simulador"
              className="block w-full text-left px-4 py-3 text-gray-300 hover:bg-gold/10 hover:text-gold rounded-md transition-colors"
              data-testid="mobile-nav-simulator"
            >
              Simulador
            </a>
            <a 
              href="/primes"
              className="block w-full text-left px-4 py-3 text-gray-300 hover:bg-gold/10 hover:text-gold rounded-md transition-colors"
              data-testid="mobile-nav-primes"
            >
              Primes
            </a>
            <a 
              href="/projectos"
              className="block w-full text-left px-4 py-3 text-gray-300 hover:bg-gold/10 hover:text-gold rounded-md transition-colors"
              data-testid="mobile-nav-projectos"
            >
              Projectos
            </a>
            <a 
              href="/business"
              className="block w-full text-left px-4 py-3 text-gray-300 hover:bg-gold/10 hover:text-gold rounded-md transition-colors"
              data-testid="mobile-nav-business"
            >
              Área de Negócio
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;